// Drawer — panel modal lateral (right) 480px. Distinto del Dialog (modal
// centrado): se usa cuando el form tiene muchos campos y queremos preservar
// contexto de la pantalla detrás (ej. C4 nueva programación de producción).
//
//   <Drawer open={open} onClose={() => setOpen(false)}>
//     <DrawerHeader>
//       <DrawerTitle>Título</DrawerTitle>
//     </DrawerHeader>
//     <DrawerBody>...</DrawerBody>
//     <DrawerFooter>...</DrawerFooter>
//   </Drawer>

import { useEffect, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../lib/utils';

interface DrawerProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  /** Ancho en px (default 480). */
  width?: number;
  className?: string;
}

export function Drawer({ open, onClose, children, width = 480, className }: DrawerProps) {
  // Cerrar con Esc
  useEffect(() => {
    if (!open) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [open, onClose]);

  // Bloquear scroll del body cuando está abierto
  useEffect(() => {
    if (!open) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 transition-opacity" aria-hidden />

      {/* Panel */}
      <aside
        className={cn(
          'relative ml-auto h-full bg-surface-elevated text-fg shadow-elevated',
          'flex flex-col',
          'animate-in slide-in-from-right duration-200',
          className,
        )}
        style={{ width: `min(${width}px, 100vw)` }}
      >
        {children}
      </aside>
    </div>
  );
}

export function DrawerHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'flex flex-shrink-0 items-center justify-between border-b border-border px-6 py-4',
        className,
      )}
      {...props}
    />
  );
}

export function DrawerTitle({ className, ...props }: HTMLAttributes<HTMLHeadingElement>) {
  return <h2 className={cn('text-base font-medium text-fg', className)} {...props} />;
}

export function DrawerBody({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('flex-1 overflow-y-auto px-6 py-4', className)} {...props} />;
}

export function DrawerFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'flex flex-shrink-0 items-center justify-end gap-2 border-t border-border px-6 py-4',
        className,
      )}
      {...props}
    />
  );
}
